import React, { useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import {
  Button,
  Card,
  Cell,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Row,
  TextArea,
  useStatusOverlay,
  H5,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { ContentCopy } from '@fs/zion-icon'
import { copyToClipboard } from '@fs/zion-frontend-friends'
import { usePageTitle } from '../../app/PageTitleContext'
import { processIds } from '../../../services/toolsService'
import { DmcText } from '../../common/DmcText'
import useNotifications from '../../../hooks/useNotifications'
import { isNotNullOrUndefinedOrEmpty, isNullOrUndefinedOrEmpty } from '../../../util/Utils'

export default () => {}

export function IdConverter() {
  usePageView({ page_detail: 'DMConsole:IdConverter' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.idConverter'))
  const { idConverterModel, idConverterActions } = useIdConverterController()

  return (
    <Grid>
      {/* Row */}
      <Cell columns={12}>
        <H5>{t('tools.lists.heading', "ID's Converter")}</H5>
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <H5 supplementary>{t('tools.idConverter.description', "Convert ID's to and from Alphanumeric and Numeric")}</H5>
      </Cell>
      {/* Row */}
      <Cell columns={2}>
        <RadioGroup orientation="vertical" label={t('tools.idConverter.conversionType', 'Conversion Type')}>
          <Radio
            value="NUMERIC"
            label={t('numeric', 'Numeric')}
            checked={idConverterModel.conversionType === 'NUMERIC'}
            onChange={(event) => idConverterActions.setConversionType(event.target.value)}
          />
          <Radio
            value="DISPLAYABLE"
            label={t('alphanumeric', 'Alphanumeric')}
            checked={idConverterModel.conversionType === 'DISPLAYABLE'}
            onChange={(event) => idConverterActions.setConversionType(event.target.value)}
          />
        </RadioGroup>
        <Button
          disabled={isNullOrUndefinedOrEmpty(idConverterModel.idList)}
          emphasis="high"
          onClick={idConverterActions.processList}
        >
          {t('tools.action.process', 'Process')}
        </Button>
      </Cell>
      <Cell columns={10}>
        <TextArea
          label={t('tools.ids', "ID's")}
          placeholder={t('tools.ids.prompt', "Enter comma separated ID's")}
          value={idConverterModel.idList}
          onChange={(event) => idConverterActions.setIdList(event.target.value)}
        />
      </Cell>
      {/* Row */}
      {isNotNullOrUndefinedOrEmpty(idConverterModel.processedIdList) && (
        <Cell columns={12}>
          <Row>
            <H5 supplementary>{t('tools.idConverter.encodedIds', "Encoded ID's")}</H5>
            <IconButton onClick={idConverterActions.onClickCopy} Icon={ContentCopy} />
          </Row>
          <Card edges="border">
            <DmcText text={idConverterModel.processedIdList} />
          </Card>
        </Cell>
      )}
    </Grid>
  )
}

export function useIdConverterController() {
  const { t } = useTranslation()
  const { showServerNotifications } = useNotifications()
  const showStatusOverlay = useStatusOverlay()

  const [idList, setIdList] = useState()
  const [conversionType, setConversionType] = useState('NUMERIC')
  const [processedIdList, setProcessedIdList] = useState()

  const idConverterModel = {
    idList,
    conversionType,
    processedIdList,
  }

  const idConverterActions = {
    setIdList,
    setConversionType,
    setProcessedIdList,
    processList: async () => {
      return processIds(
        idConverterModel.conversionType,
        idConverterModel.idList.split(/,\n|;\n|\n|,|;|\s+/g).filter((id) => id !== '')
      ).then((data) => {
        idConverterActions.setProcessedIdList(data.ids.join(', '))
        showServerNotifications(data.notifications)
        return Promise.resolve()
      })
    },
    onClickCopy: () => {
      copyToClipboard(idConverterModel.processedIdList)
      showStatusOverlay({
        message: t('tools.copiedToClipboardWithIds', {
          defaultValue: `Copied to clipboard: ${idConverterModel.processedIdList}`,
          ids: idConverterModel.processedIdList,
        }),
        dismissable: true,
        type: 'normal',
      })
    },
  }

  return { idConverterModel, idConverterActions }
}
